import {
	breakpoints,
	Button,
	colors,
	Flex,
	scale,
	Text,
} from "@capioweb/ui-components/src"
import React from "react"
import styled from "styled-components"
import {
	BooleanProperty,
	StringListProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { LocalesContext } from "../../context"
import {
	imageBlockDataToImageProps,
	sendCommonSearchesDataLayerPush,
	urlIsInternal,
} from "../../utils"
import { QBankImage } from "../../@types/CapioWeb/core"
import { SearchComponent } from "./components/SearchComponent"

export interface SearchHeroBlockProps {
	heading: StringProperty
	backgroundImageBlock?: QBankImage
	hideSearch?: BooleanProperty
	tagline?: StringProperty
	preamble?: StringProperty
	commonSearches?: CommonSearch[]
	commonSearchesTitle?: StringListProperty
	query?: string
	marginBottom?: BooleanProperty
}

export interface CommonSearch {
	text: string
	link: string
}

const StyledShapeDesktop = styled.svg`
	display: none;
	position: absolute;
	height: calc(100% + 1px);
	top: -1px;
	left: -1px;
	@media screen and (min-width: ${breakpoints.l}px) {
		display: block;
	}
`
const StyledShapeMobile = styled.svg`
	position: absolute;
	width: 100%;
	bottom: -2px;
	@media screen and (min-width: ${breakpoints.l}px) {
		display: none;
	}
`

const SearchHeroBlock: React.FC<SearchHeroBlockProps> = ({
	heading,
	preamble,
	query,
	commonSearches,
	commonSearchesTitle,
	marginBottom,
	backgroundImageBlock,
	tagline,
	hideSearch,
}) => {
	const [showMore, setShowMore] = React.useState(false)
	const [showLink, setShowLink] = React.useState(false)

	const { translate, translationKeys } = React.useContext(LocalesContext)

	const searchUrl = (query: string) => `/sok?query=${query}`

	const srcSetSizes = [
		{ width: 1280, breakpoint: 1280 },
		{ width: 1920, breakpoint: 1920 },
		{ width: 2560, breakpoint: 2560 },
	]

	const contentRef = React.createRef<HTMLDivElement>()
	React.useLayoutEffect(() => {
		if (contentRef?.current) {
			if (contentRef.current.clientHeight < contentRef.current.scrollHeight) {
				setShowLink(true)
			}
		}
	}, [contentRef])

	return (
		<Flex w={1} mb={marginBottom?.value ? 3 : 0}>
			<Flex
				backgroundColor="blue11"
				w={1}
				flexDirection={{ _: "column", l: "row" }}
			>
				<Flex
					w={1}
					flexBasis={{ l: "50%" }}
					order={{ _: 1, l: 0 }}
					pl={{ l: 3 }}
				>
					<Flex
						w={1}
						flexDirection="column"
						pt={{ _: 4.5, l: 10 }}
						pr={{ _: 3, l: 0 }}
						pb={{ _: 4.5, l: showMore ? 4.5 : 8.75 }}
						pl={{ _: 3, l: "calc(50vw - 588px)" }}
					>
						<Flex
							flexDirection="column"
							mr={{ l: 3 }}
							maxw={{ _: "none", l: scale.px(57) }}
						>
							<Text
								textAlign={{ _: "center", l: "left" }}
								variant="preamble"
								mb={0}
							>
								{tagline?.value}
							</Text>
							<Text
								textAlign={{ _: "center", l: "left" }}
								mb={hideSearch ? 2 : 4}
								variant="headline-1"
								fontSize={{ _: 4, l: 6 }}
								lineHeight={{ _: 4, l: 6 }}
							>
								{heading?.value}
							</Text>
							{hideSearch?.value ? (
								<>
									{preamble?.value && (
										<Flex
											ref={contentRef}
											w={1}
											h={showMore ? "auto" : { _: 6, m: 8, l: 12, xl: 16 }}
											overflow="hidden"
										>
											<Text
												mb={0}
												variant="paragraph-16"
												fontSize={{ _: 2, m: 2.5 }}
												lineHeight={{ _: 3, m: 4 }}
												textAlign={{ _: "center", l: "left" }}
											>
												{preamble.value}
											</Text>
										</Flex>
									)}
								</>
							) : (
								<SearchComponent
									query={query}
									isStartPage
									commonSearches={commonSearches}
									commonSearchesTitle={commonSearchesTitle}
								/>
							)}
						</Flex>
						{showLink && hideSearch?.value && (
							<Text.Button
								fontSize={{ _: 2, m: 2.5 }}
								lineHeight={{ _: 3, m: 4 }}
								textAlign={{ _: "center", l: "left" }}
								mb={0}
								onClick={() => setShowMore(!showMore)}
								style={{ textDecoration: "underline" }}
							>
								{showMore
									? translate(translationKeys.showLess)
									: translate(translationKeys.showMore)}
							</Text.Button>
						)}
					</Flex>
				</Flex>

				<Flex position="relative" w={{ _: 1, l: 0.5 }} h={{ _: 30, l: "auto" }}>
					<img
						data-epi-edit="backgroundImageBlock"
						{...imageBlockDataToImageProps(
							backgroundImageBlock,
							true,
							true,
							srcSetSizes
						)}
						style={{
							objectFit: "cover",
							width: "100%",
						}}
					/>
					<StyledShapeDesktop viewBox="0 0 200 384" preserveAspectRatio="none">
						<path
							d="M1 192C1 107 53 0 53 0H0V384H53C53 384 1 277 1 192Z"
							fill="#F0F4FA"
						/>
					</StyledShapeDesktop>
					<StyledShapeMobile viewBox="0 0 390 32" preserveAspectRatio="none">
						<path
							d="M84.5 29.3731C22 25.8299 0 15.749 0 15.749V31.4513H390V15.749C390 15.749 366 2.01743 298 0.50072C230 -1.01599 147 32.9164 84.5 29.3731Z"
							fill="#F0F4FA"
						/>
					</StyledShapeMobile>
				</Flex>
			</Flex>
		</Flex>
	)
}
export { SearchHeroBlock }
